'use client';

import React from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';

export interface Tab {
  id: string;
  label: string;
}

export interface TabControllerProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
  /** `accent` = orange/red pill active state (e.g. Log Monitor). `accentToolbar` = split pills, inactive with border (Log Analyzer toolbar). Default `neutral` preserves legacy gray pills. */
  variant?: 'neutral' | 'accent' | 'accentToolbar';
}

const TabsContainer = styled.div<{ $variant: 'neutral' | 'accent' | 'accentToolbar' }>`
  display: flex;
  gap: ${({ $variant }) => ($variant === 'accentToolbar' ? '8px' : '4px')};
  max-width: fit-content;
  background-color: ${({ $variant }) =>
    $variant === 'accent' || $variant === 'accentToolbar'
      ? 'transparent'
      : theme.colors.gray.neutral};
  border-radius: ${theme.borderRadius.lg};
  padding: ${({ $variant }) => ($variant === 'accentToolbar' ? 0 : '4px')};
  margin-bottom: ${({ $variant }) =>
    $variant === 'accent' || $variant === 'accentToolbar' ? 0 : '24px'};
`;

const Tab = styled.button<{
  $active: boolean;
  $size?: 'sm' | 'md';
  $variant: 'neutral' | 'accent' | 'accentToolbar';
}>`
  border: ${({ $active, $variant }) => {
    if ($variant === 'accentToolbar') {
      return $active ? 'none' : `1px solid ${theme.colors.gray[200]}`;
    }
    return 'none';
  }};
  background-color: ${({ $active, $variant }) => {
    if ($variant === 'accentToolbar') {
      return $active ? theme.colors.primary.accentDark : theme.colors.white;
    }
    if ($variant === 'accent') {
      return $active ? theme.colors.primary.accentDark : 'white';
    }
    return $active ? theme.colors.gray[50] : 'transparent';
  }};
  color: ${({ $active, $variant }) => {
    if ($variant === 'accentToolbar' || $variant === 'accent') {
      return $active ? theme.colors.white : theme.colors.gray.darkText;
    }
    return $active ? theme.colors.gray.darkText : theme.colors.border.primary;
  }};
  border-radius: ${theme.borderRadius.full};
  cursor: pointer;
  font-weight: ${props => (props.$active ? '600' : '400')};
  font-size: 14px;
  transition: all 0.2s ease;

  /* Size variants */
  ${props => {
    if (props.$size === 'sm') {
      return `
        padding: 2.5px 12px;
        height: 24px;
        border-radius: ${theme.borderRadius.lg};
      `;
    }
    // md (default)
    return `
      padding: 10.5px 20px;
    `;
  }}

  &:hover {
    color: ${({ $active, $variant }) => {
    if ($variant === 'accentToolbar' || $variant === 'accent') {
      return $active ? theme.colors.white : theme.colors.gray.darkText;
    }
    return theme.colors.gray.darkText;
  }};
    background-color: ${({ $active, $variant }) => {
    if ($variant === 'accentToolbar') {
      return $active ? theme.colors.primary.accentDark : theme.colors.gray[50];
    }
    if ($variant === 'accent') {
      return $active ? theme.colors.primary.accentDark : theme.colors.gray[100];
    }
    return $active ? theme.colors.white : theme.colors.gray[50];
  }};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary.accent};
    outline-offset: 2px;
  }
`;

/**
 * TabController Component
 * 
 * A tab controller component for switching between different content views.
 * 
 * @component
 * @example
 * ```tsx
 * <TabController
 *   tabs={[
 *     { id: 'tab1', label: 'Tab 1' },
 *     { id: 'tab2', label: 'Tab 2' }
 *   ]}
 *   activeTab="tab1"
 *   onTabChange={(id) => setActiveTab(id)}
 * />
 * ```
 */
export const TabController: React.FC<TabControllerProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  size = 'md',
  style,
  variant = 'neutral',
}: TabControllerProps) => {
  // Generate stable ID based on tabs to avoid hydration mismatches
  // Use a combination of tab IDs to create a deterministic ID that's the same on server and client
  const stableId = React.useMemo(() => {
    if (tabs?.length === 0) return 'tablist-default';
    // Create a stable ID from sorted tab IDs to ensure consistency
    const tabIds = [...tabs].sort((a, b) => a.id.localeCompare(b.id)).map(t => t.id).join('-');
    // Sanitize to create a valid HTML ID
    const sanitized = tabIds.replace(/[^a-zA-Z0-9-]/g, '-').toLowerCase();
    return `tablist-${sanitized}`;
  }, [tabs]);

  return (
    <TabsContainer
      className={className}
      style={style}
      $variant={variant}
      role="tablist"
      id={stableId}
      aria-label="Navigation tabs"
    >
      {tabs.map((tab) => {
        const tabId = `tab-${tab.id}`;
        const panelId = `tabpanel-${tab.id}`;

        return (
          <Tab
            key={tab.id}
            id={tabId}
            $active={activeTab === tab.id}
            $size={size}
            $variant={variant}
            onClick={() => onTabChange(tab.id)}
            aria-selected={activeTab === tab.id ? 'true' : 'false'}
            aria-controls={panelId}
            role="tab"
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            {tab.label}
          </Tab>
        );
      })}
    </TabsContainer>
  );
};

export default TabController;

